import type { VocabEntry } from "../../data/vocab/types";
import { WordAudioButton } from "./WordAudioButton";
import { lemmaWithoutArticle } from "./word-helpers";

type WordListProps = {
  entries: VocabEntry[];
  selectedId: string | undefined;
  onSelect: (id: string) => void;
};

/** Left pane of the master-detail browser: a scrollable, selectable list of
 *  words. The selected row gets an accent-soft fill; picking a row calls
 *  onSelect so the detail pane swaps. The play button is an isolated control
 *  (its own button) so tapping audio never changes the selection. The english
 *  gloss is hidden below lg to keep the narrow mobile row readable. */
export function WordList({ entries, selectedId, onSelect }: WordListProps) {
  return (
    <ul className="space-y-0.5">
      {entries.map((entry) => {
        const selected = entry.id === selectedId;
        const lemma = lemmaWithoutArticle(entry.dutch);
        return (
          <li key={entry.id}>
            <div
              role="button"
              tabIndex={0}
              aria-pressed={selected}
              onClick={() => onSelect(entry.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onSelect(entry.id);
                }
              }}
              className={`flex items-center gap-3 rounded-[var(--radius)] px-3 py-2.5 cursor-pointer transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)] focus-visible:outline-offset-2 ${
                selected ? "bg-[var(--accent-soft)]" : "hover:bg-[var(--panel-soft)]"
              }`}
            >
              <WordAudioButton text={entry.dutch} variant="icon" iconSize="md" />
              <span className="min-w-0 flex-1 truncate">
                <span className="font-semibold text-[var(--ink)]">{lemma}</span>
                {entry.gender ? (
                  <span className="ml-1.5 text-sm text-[var(--ink-faint)]">{entry.gender}</span>
                ) : null}
              </span>
              <span className="hidden lg:inline shrink-0 text-sm text-[var(--ink-muted)] truncate max-w-[45%] text-right">
                {entry.english}
              </span>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
